import React from 'react';
import { FiEdit2, FiChevronLeft, FiChevronRight, FiFileText, FiBox } from 'react-icons/fi';
import { FaBuilding, FaQuestionCircle } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useLocalState from '../hooks/useLocalState';

export default function SupplierInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();

  // Using v4 cache key to match PurchaseRecords update
  const [invoices] = useLocalState('purchase_invoices_data_v4', []);

  // Find the requested invoice, fallback to the first one if not found
  const invoice = invoices.find(inv => inv.id === id) || invoices[0];

  if (!invoice) {
    return <div className="p-10 text-center text-gray-500">Invoice not found.</div>;
  }

  return (
    <div className="flex flex-col min-h-full bg-white font-sans">
      {/* Sub Navigation Tabs */}
      <div className="flex bg-[#a6e4e7] pt-2 px-0 shadow-sm relative z-10 border-b border-[#8ed1d4] shrink-0">
        <div className="px-4 py-2 bg-white text-gray-800 text-[13px] flex items-center shadow-[0_-2px_4px_rgba(0,0,0,0.05)] rounded-t-sm ml-2 font-medium">
          <FiFileText className="mr-1.5 text-gray-500" />
          Supplier invoice
        </div>
        <div className="px-4 py-2 text-gray-700 text-[13px] hover:bg-white/30 cursor-pointer transition-colors">Contacts/Addresses</div>
        <div className="px-4 py-2 text-gray-700 text-[13px] hover:bg-white/30 cursor-pointer transition-colors">Credit transfer</div>
        <div className="px-4 py-2 text-gray-700 text-[13px] hover:bg-white/30 cursor-pointer transition-colors">MoceanAPI SMS</div>
        <div className="px-4 py-2 text-gray-700 text-[13px] hover:bg-white/30 cursor-pointer transition-colors">Notes</div>
        <div className="px-4 py-2 text-gray-700 text-[13px] hover:bg-white/30 cursor-pointer transition-colors">Linked files</div>
      </div>

      <div className="flex-1 overflow-y-auto relative flex flex-col">
        <div className="p-6 flex-1">
          {/* Header Section */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-start space-x-4">
              <div className="w-[42px] h-[46px] bg-white border border-gray-200 shadow-sm flex flex-col relative overflow-hidden shrink-0">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#5bc0be]"></div>
                <div className="flex-1 flex items-center justify-center text-teal-600 font-black text-[11px] leading-none pt-1 pl-1">EL</div>
                <div className="flex-1 flex items-center justify-center text-teal-600 font-black text-[13px] leading-none pb-1 pl-1">$</div>
              </div>
              <div>
                <h2 className="text-[22px] font-bold text-[#008080] leading-none mb-2">{invoice.ref}</h2>
                <div className="text-[13px] grid grid-cols-[100px_1fr] gap-y-1.5 gap-x-2 items-center">
                  <div className="text-gray-600 flex items-center">
                    Ref. supplier <FiEdit2 className="ml-1 text-gray-300 w-3 h-3" /> <span className="ml-1">:</span>
                  </div>
                  <div className="text-gray-800">{invoice.refSup}</div>
                  <div className="text-gray-600 flex items-center">
                    Third-party <span className="ml-1">:</span>
                  </div>
                  <div className="text-gray-800 flex items-center font-medium">
                    <FaBuilding className="text-[#000080] mr-1.5" />
                    {invoice.third} (Other invoices)
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-end space-y-3">
              <div className="flex items-center space-x-3 text-[14px]">
                <Link to="/purchase/records" className="font-bold text-[#0000a0] hover:underline">Back to list</Link>
                <div className="flex items-center space-x-2">
                  <FiChevronLeft className="text-gray-300 text-xl cursor-pointer hover:opacity-70" />
                  <FiChevronRight className="text-black text-xl cursor-pointer hover:opacity-70" />
                </div>
              </div>
              <div className={`border px-4 py-1.5 text-[13px] bg-white rounded-sm shadow-sm ${invoice.status === 'Draft' ? 'border-gray-200 text-gray-400' : 'border-[#f0ad4e] text-gray-800 font-medium'}`}>
                {invoice.status === 'Draft' ? 'Draft (needs to be validated)' : 'Validated'}
              </div>
              <div className="text-gray-400 text-[12px]">
                Not yet transferred to accounting
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12">
            <div>
              <table className="w-full text-[13px] border-none">
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none w-[35%]">Type</td>
                    <td className="py-2.5 border-none text-gray-800">
                      <span className="bg-gray-100 px-2 py-0.5 rounded-[3px] border border-gray-200 text-[12px]">Standard invoice</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none align-top pt-3">Discounts</td>
                    <td className="py-2.5 border-none text-gray-400 text-[12px] leading-relaxed">
                      You have no default relative discount from this supplier.<br/>
                      You have no discount credit available from this supplier.<br/>
                      <span className="text-gray-800 font-medium cursor-pointer hover:underline">(Edit absolute discounts)</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none flex items-center">Invoice date <FiEdit2 className="ml-1 text-gray-300 w-3 h-3" /></td>
                    <td className="py-2.5 border-none text-gray-800">{invoice.date}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none flex items-center">Payment Terms <FiEdit2 className="ml-1 text-gray-300 w-3 h-3" /></td>
                    <td className="py-2.5 border-none text-gray-800">Due Upon Receipt</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none flex items-center">Payment due on <FiEdit2 className="ml-1 text-gray-300 w-3 h-3" /></td>
                    <td className="py-2.5 border-none text-gray-800">{invoice.due || invoice.date}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none flex items-center">Payment method <FiEdit2 className="ml-1 text-gray-300 w-3 h-3" /></td>
                    <td className="py-2.5 border-none text-gray-800">Bank Transfer</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none flex items-center">Currency <FiEdit2 className="ml-1 text-gray-300 w-3 h-3" /></td>
                    <td className="py-2.5 border-none text-gray-800">HKD - Hong Kong Dollar</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 lg:mt-0">
              <table className="w-full text-[13px] border-none mb-6">
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none w-1/2">Amount (excl. tax)</td>
                    <td className="py-2.5 border-none text-right text-gray-800 font-medium">${invoice.amtExcl}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none">Amount tax</td>
                    <td className="py-2.5 border-none text-right flex justify-end items-center space-x-2">
                      <span className="text-gray-800 font-medium">$0.00</span>
                      <span className="text-gray-500 text-[11px] flex items-center cursor-pointer hover:text-gray-700">
                        Recalculate Method 1 / Method 2 <FaQuestionCircle className="ml-1 text-gray-300" />
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-500 border-none font-bold">Amount (inc. tax)</td>
                    <td className="py-2.5 border-none text-right text-gray-800 font-bold">${invoice.amtInc}</td>
                  </tr>
                </tbody>
              </table>

              <table className="w-full text-[11px] mb-6 border-collapse">
                <thead>
                  <tr className="bg-[#a6e4e7] border-none text-gray-700">
                    <th className="py-1.5 px-3 border-r border-white font-bold text-left">Payments</th>
                    <th className="py-1.5 px-3 border-r border-white font-bold text-left">Date</th>
                    <th className="py-1.5 px-3 border-r border-white font-bold text-left">Type</th>
                    <th className="py-1.5 px-3 border-r border-white font-bold text-left">Bank account</th>
                    <th className="py-1.5 px-3 font-bold text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="5" className="py-3 px-3 text-gray-400 bg-[#f9f9f9] text-left border-b border-gray-200 italic">None</td>
                  </tr>
                </tbody>
              </table>

              <table className="w-full text-[13px] border-none">
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-400 border-none text-right pr-6">Already paid (without credit notes and down payments)</td>
                    <td className="py-2.5 border-none text-right text-gray-800">{invoice.paid}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 text-gray-400 border-none text-right pr-6 font-medium">Billed</td>
                    <td className="py-2.5 border-none text-right text-gray-800 font-medium">{invoice.amtInc}</td>
                  </tr>
                  <tr>
                    <td className="py-4 text-gray-500 border-none text-right pr-6 font-bold">Remaining unpaid</td>
                    <td className="py-4 border-none text-right text-[#cc0000] font-bold text-[32px] leading-none">{invoice.remain}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Description Table (Dynamically rendered based on items) */}
          <div className="mt-8 overflow-x-auto">
            <table className="min-w-full text-[12px] border-collapse">
              <thead>
                <tr className="bg-[#a6e4e7] border-none text-gray-700">
                  <th className="py-2 px-3 font-normal text-left">Description</th>
                  <th className="py-2 px-3 font-normal text-right">TAX</th>
                  <th className="py-2 px-3 font-normal text-right">U.P. (net)</th>
                  <th className="py-2 px-3 font-normal text-center">Qty</th>
                  <th className="py-2 px-3 font-normal text-right">Disc.</th>
                  <th className="py-2 px-3 font-normal text-right">Total (excl.)</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items && invoice.items.length > 0 ? (
                  invoice.items.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-2.5 px-3 border-none text-gray-800">
                        <div className="font-medium">
                          <FiBox className="inline mr-1.5 text-[#a08a50] text-sm" />
                          {item.desc}
                        </div>
                        {item.extra && (
                          <div className="text-gray-500 text-[11px] mt-0.5 ml-6">{item.extra}</div>
                        )}
                      </td>
                      <td className="py-2.5 px-3 border-none text-right text-gray-800">{item.tax}</td>
                      <td className="py-2.5 px-3 border-none text-right text-gray-800">{item.up}</td>
                      <td className="py-2.5 px-3 border-none text-center text-gray-800">{item.qty !== 0 ? item.qty : ''}</td>
                      <td className="py-2.5 px-3 border-none text-right text-gray-800">{item.disc !== '0%' ? item.disc : ''}</td>
                      <td className="py-2.5 px-3 border-none text-right text-gray-800 font-medium">{item.total !== '0.00' ? item.total : ''}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-4 text-center text-gray-500 italic">No items found for this invoice.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Bar */}
        <div className="bg-[#a6e4e7] p-3 flex justify-center space-x-2 border-t border-[#8ed1d4] mt-auto shrink-0">
          <button className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-1.5 rounded-[3px] text-[11px] font-bold tracking-wide transition-colors border border-gray-300 shadow-sm">VALIDATE</button>
          <button onClick={() => navigate('/invoice/pay-supplier')} className="bg-white hover:bg-blue-50 text-[#0000a0] px-4 py-1.5 rounded-[3px] text-[11px] font-bold tracking-wide transition-colors border border-[#0000a0] shadow-sm">
            ENTER PAYMENT
          </button>
          <button className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-1.5 rounded-[3px] text-[11px] font-bold tracking-wide transition-colors border border-gray-300 shadow-sm">CLONE</button>
          <button className="bg-white hover:bg-red-50 text-red-600 px-4 py-1.5 rounded-[3px] text-[11px] font-bold tracking-wide transition-colors border border-gray-300 shadow-sm">DELETE</button>
        </div>
      </div>
    </div>
  );
}